import React, { useState } from 'react';
import { authService, AuthUser } from '../../services/auth.service';
import styles from './ChangePassword.module.css';

interface ChangePasswordProps {
  user: AuthUser;
  onClose: () => void;
}

const ChangePassword: React.FC<ChangePasswordProps> = ({ user, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authService.changePassword(currentPassword, newPassword);
      setSuccess('Password changed successfully!');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Change Password</h2>
          <button 
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>
        </div>

        {/* User Info */}
        <div className={styles.userInfo}>
          <div className={styles.userIcon}>
            <img 
              src={`${process.env.PUBLIC_URL}/images/humanicon.png`}
              alt="User Avatar"
              className={styles.userImage}
            />
          </div>
          <div className={styles.userDetails}>
            <p className={styles.userEmail}>{user.email}</p>
            <p className={styles.userNote}>Update your account password</p>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className={styles.successMessage}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2"/>
              <polyline points="22,4 12,14.01 9,11.01" stroke="currentColor" strokeWidth="2"/>
            </svg>
            {success}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className={styles.errorMessage}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
              <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
            </svg>
            {error}
          </div>
        )}

        {/* Change Password Form */}
        <form onSubmit={handleSubmit} className={styles.changePasswordForm}>
          {/* Current Password Field */}
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Current Password</label>
            <div className={styles.inputContainer}>
              <span className={styles.inputIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#38b2a8" strokeWidth="2"/>
                  <circle cx="12" cy="16" r="1" fill="#38b2a8"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#38b2a8" strokeWidth="2"/>
                </svg>
              </span>
              <input
                type={showCurrentPassword ? "text" : "password"}
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={loading}
                className={styles.inputField}
                required
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#999" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="3" stroke="#999" strokeWidth="2"/>
                  {!showCurrentPassword && <path d="M3 3l18 18" stroke="#999" strokeWidth="2"/>}
                </svg>
              </button>
            </div>
          </div>

          {/* New Password Field */}
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>New Password</label>
            <div className={styles.inputContainer}>
              <span className={styles.inputIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#38b2a8" strokeWidth="2"/>
                  <circle cx="12" cy="16" r="1" fill="#38b2a8"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#38b2a8" strokeWidth="2"/>
                </svg>
              </span>
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
                className={styles.inputField}
                required
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#999" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="3" stroke="#999" strokeWidth="2"/>
                  {!showNewPassword && <path d="M3 3l18 18" stroke="#999" strokeWidth="2"/>}
                </svg>
              </button>
            </div>
          </div>

          {/* Confirm Password Field */}
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Confirm New Password</label>
            <div className={styles.inputContainer}>
              <span className={styles.inputIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#38b2a8" strokeWidth="2"/>
                  <circle cx="12" cy="16" r="1" fill="#38b2a8"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#38b2a8" strokeWidth="2"/>
                </svg>
              </span>
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                className={styles.inputField}
                required
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#999" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="3" stroke="#999" strokeWidth="2"/>
                  {!showConfirmPassword && <path d="M3 3l18 18" stroke="#999" strokeWidth="2"/>}
                </svg>
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className={styles.passwordRequirements}>
            <p className={styles.requirementsTitle}>Password Requirements:</p>
            <ul className={styles.requirementsList}>
              <li className={newPassword.length >= 6 ? styles.valid : styles.invalid}>
                At least 6 characters
              </li>
              <li className={newPassword !== currentPassword ? styles.valid : styles.invalid}>
                Different from current password
              </li>
              <li className={newPassword === confirmPassword && confirmPassword !== '' ? styles.valid : styles.invalid}>
                Passwords match
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <button 
              type="button" 
              className={styles.cancelButton}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className={styles.changePasswordButton}
              disabled={loading}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;